import { resolveApiOrigin } from '@/lib/app-config';
import {
  clearStoredToken,
  readStoredToken,
  writeStoredToken,
} from '@/lib/auth-token';

export { AUTH_TOKEN_KEY } from '@/lib/auth-token';
const FETCH_TIMEOUT_MS = 12_000;

export function getToken(): string | null {
  return readStoredToken();
}

export async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<{ data?: T; error?: string; details?: unknown }> {
  const apiOrigin = resolveApiOrigin();
  if (!apiOrigin) {
    return { error: 'API URL not set. Add VITE_API_URL to .env and restart the dev server (or rebuild the app).' };
  }
  const token = getToken();
  const url = `${apiOrigin}/api${path.startsWith('/') ? path : `/${path}`}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, { ...options, headers, signal: controller.signal });
    clearTimeout(timeoutId);
    const text = await res.text();
    const json = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};

    if (!res.ok) {
      const anyJson = json as { error?: string; message?: string; errors?: Record<string, string[]> };
      const firstErrorsMsg = anyJson.errors ? Object.values(anyJson.errors)[0]?.[0] : undefined;
      return {
        error: anyJson.error ?? anyJson.message ?? firstErrorsMsg ?? `Request failed (${res.status})`,
        details: json,
      };
    }

    const payload = json as { error?: string; message?: string; leaveRequest?: unknown };
    if (payload.error) {
      return { error: payload.error, details: json };
    }

    return { data: json as T };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error) {
      if (err.name === 'AbortError') return { error: 'Request timed out. Check your connection and try again.' };
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))
        return {
          error:
            'Cannot reach API. Check: (1) backend running (Docker: npm run staging:up → http://127.0.0.1, or artisan serve → :8001); (2) VITE_API_URL matches that host/port; (3) on a physical phone use your Mac LAN IP, not localhost; (4) restart npm run dev after .env changes.',
        };
      return { error: err.message };
    }
    return { error: 'Something went wrong' };
  }
}

export function clearToken(): void {
  clearStoredToken();
}

export function setToken(token: string, remember = true): void {
  writeStoredToken(token, remember);
}
